/**
 * What kind of device this is, as coarsely as the privacy page promises: a phone, a tablet or a
 * computer, never the model, and the width of the window in four steps. The browser is the only
 * place that knows, so the page reports it; every pure part takes its readings as arguments.
 */

export type DeviceClass = 'phone' | 'tablet' | 'desktop'
export type WidthBucket = 'xs' | 'sm' | 'md' | 'lg'

/**
 * A touch screen is a phone under 600 CSS pixels on its short side, a tablet above; anything else
 * is a computer, including a laptop with a touch screen (its primary pointer is the mouse).
 */
export function classifyDevice(coarsePointer: boolean, shortSide: number): DeviceClass {
	if (!coarsePointer) return 'desktop'
	return shortSide < 600 ? 'phone' : 'tablet'
}

export function bucketWidth(px: number): WidthBucket {
	if (px < 480) return 'xs'
	if (px < 768) return 'sm'
	if (px < 1024) return 'md'
	return 'lg'
}

export function deviceClass(): DeviceClass {
	const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
	return classifyDevice(coarse, Math.min(screen.width, screen.height))
}

export const widthBucket = (): WidthBucket => bucketWidth(window.innerWidth)

/** The low-end flag app.html computed before the app loaded: fewer animations, so a slower device. */
export const isLite = (): boolean => document.documentElement.dataset.motion === 'lite'

/** A browser driven by a script says so (`navigator.webdriver`). Headless ones that hide it count as people. */
export const isAutomated = (): boolean => navigator.webdriver === true
